import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Col, Form, Modal, Nav, Row, Table} from 'react-bootstrap';
import {toast} from 'react-toastify';
import {AxiosResponse} from "axios";
import {useApiService} from "../../context/ApiServiceContext";
import * as yup from 'yup';
import {useFormik} from "formik";
import FAQ, {DEFAULT_FAQ_SECTION, FAQ_SECTION_LABELS, FAQ_SECTIONS, FaqSection, toFaqSection} from "../../models/FAQ";
import ReactQuill from "react-quill";
import {makeStyles} from "@material-ui/core";
import {moveItem} from "../../utils/arrayUtils";

interface FAQRequestBody {
    title: string;
    description: string;
    section: FaqSection;
}

const useStyles = makeStyles({
    quillRtl: {
        "& .ql-editor": {
            minHeight: "calc(1.5em * 10)",
            maxHeight: "calc(1.5em * 20)",
            direction: "rtl",
            textAlign: "right"
        }
    }
})

const modules = {
    toolbar: [
        [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
        [{size: []}],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'},
            {'indent': '-1'}, {'indent': '+1'}],
        ['clean']
    ],
    clipboard: {
        // toggle to add extra line breaks when pasting HTML:
        matchVisual: false,
    }
};

// Formats the react-quill editor understands
const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent'
];

const FAQS_PER_PAGE = 100;
// Safety net so a misbehaving server can never spin the fetch loop forever
const MAX_FAQ_PAGES = 50;

const AdminFAQ: React.FC = () => {
    const classes = useStyles();
    const apiService = useApiService();
    const [faqs, setFAQ] = useState<FAQ[]>([]);
    const [selectedFAQ, setSelectedFAQ] = useState<FAQ | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    // Every list read, every reorder and every new question is scoped to the tab
    // the admin is looking at - the two sections are ordered independently.
    const [activeSection, setActiveSection] = useState<FaqSection>(DEFAULT_FAQ_SECTION);
    const [faqFields, setFAQFields] = useState<FAQRequestBody>({
        title: '',
        description: '',
        section: DEFAULT_FAQ_SECTION,
    });

    // Synchronous mirror of savingOrder - state alone can still be read as `false`
    // by a second click fired in the same frame as the first one
    const savingOrderRef = useRef(false);

    // The whole list is loaded at once - manual ordering needs every id in a single array.
    // Returns whether the list was actually refreshed, so callers can tell the user the truth.
    const fetchFAQ = useCallback(async (): Promise<boolean> => {
        try {
            const allFAQ: FAQ[] = [];
            for (let page = 1; page <= MAX_FAQ_PAGES; page++) {
                const url = `/faq/list?section=${activeSection}&page=${page}&per_page=${FAQS_PER_PAGE}`;
                const res = await apiService.api.get(url);
                allFAQ.push(...res.data);
                if (res.data.length < FAQS_PER_PAGE) {
                    break;
                }
            }
            setFAQ(allFAQ);
            return true;
        } catch (error) {
            return false;
        }
    }, [apiService, activeSection]);

    useEffect(() => {
        fetchFAQ();
    }, [fetchFAQ]);

    const handleReorder = async (from: number, to: number) => {
        if (savingOrderRef.current || from === to) {
            return;
        }

        const reordered = moveItem(faqs, from, to);
        setFAQ(reordered);
        savingOrderRef.current = true;
        setSavingOrder(true);

        try {
            // The section scopes both the reorder and the server's 409 set-check,
            // so the ids sent here only ever have to cover the tab on screen.
            const res: AxiosResponse<FAQ[]> = await apiService.api.put('/faq/order', {
                orderedIds: reordered.map((faq) => faq.id),
                section: activeSection,
            });
            setFAQ(res.data);
        } catch (error: any) {
            const isStaleList = error?.response?.status === 409;
            const refreshed = await fetchFAQ();

            if (!refreshed) {
                toast.error('בעיה בטעינת הרשימה, רענן את הדף');
            } else if (isStaleList) {
                toast.error('רשימת השאלות השתנתה בינתיים והרשימה רועננה, נסה שוב');
            } else {
                toast.error('בעיה בשמירת סדר השאלות');
            }
        } finally {
            savingOrderRef.current = false;
            setSavingOrder(false);
        }
    };

    const handleSectionChange = (section: FaqSection) => {
        // Reordering rewrites the list the save is about to answer with - never
        // let the tab move out from under it
        if (savingOrderRef.current || section === activeSection) {
            return;
        }

        // The rows on screen belong to the tab being left; a failed fetch may not
        // leave them behind as if they were the new tab's.
        setFAQ([]);
        setActiveSection(section);
    };

    const handleDelete = (faqId: string) => {
        // A delete resolving mid-reorder would be undone by the order response
        if (savingOrderRef.current) {
            return;
        }

        if (window.confirm('האם אתה בטוח שברצונך למחוק את השאלה?')) {
            apiService.api
                .delete(`/faq/${faqId}`)
                .then(() => {
                    setFAQ(prevFAQ => prevFAQ.filter((faq) => faq.id !== faqId));
                    toast.success('השאלה נמחקה בהצלחה');
                })
                .catch(() => {
                    toast.error('בעיה במחיקת השאלה');
                });
        }
    };

    // A question that moved to the other section cannot stay in the list on
    // screen. Following it there beats silently dropping the row the admin just
    // saved - the tab switch refetches through the activeSection effect.
    const followToSection = (section: FaqSection): boolean => {
        if (section === activeSection) {
            return false;
        }

        setFAQ([]);
        setActiveSection(section);
        return true;
    };

    const handleUpdate = (values: any) => {
        if (selectedFAQ) {
            const updatedFAQ: FAQRequestBody = {
                ...values
            };

            apiService.api
                .put(`/faq/${selectedFAQ.id}`, updatedFAQ)
                .then((res: AxiosResponse<FAQ>) => {
                    toast.success('השאלה עודכנה בהצלחה');
                    if (followToSection(toFaqSection(res.data.section))) {
                        return;
                    }
                    setFAQ(prevFAQ =>
                        prevFAQ.map((faq) => (faq.id === selectedFAQ.id ? res.data : faq))
                    );
                })
                .catch(() => {
                    toast.error('בעיה בעדכון השאלה');
                });

            setShowModal(false);
            formik.resetForm()
        }
    };

    const handleCreate = (values: any) => {
        const newFAQ: FAQRequestBody = values;

        apiService.api
            .post('/faq/', newFAQ)
            .then(async () => {
                // Refetch instead of appending locally - the server decides where a new
                // FAQ lands in the manual order (legacy FAQs without `order` sort last)
                toast.success('השאלה נוספה בהצלחה');
                if (followToSection(toFaqSection(newFAQ.section))) {
                    return;
                }
                if (!(await fetchFAQ())) {
                    toast.error('בעיה בטעינת הרשימה, רענן את הדף');
                }
            })
            .catch(() => {
                toast.error('בעיה בהוספת השאלה');
            });

        setShowModal(false);
        formik.resetForm()
    };

    const openModal = (faq: FAQ | null) => {
        setSelectedFAQ(faq);
        setFAQFields({
            title: faq ? faq.title : '',
            description: faq ? faq.description : '',
            // A new question belongs to the tab it was opened from
            section: faq ? toFaqSection(faq.section) : activeSection,
        });
        setShowModal(true);
    };

    const faqSchema = yup.object().shape({
        title: yup.string().required('נדרשת שאלה'),
        description: yup.string().required('נדרשת תשובה'),
        section: yup.string().required('נדרש עמוד').oneOf([...FAQ_SECTIONS], 'נדרש עמוד'),
    });

    const formik = useFormik({
        initialValues: faqFields,
        validationSchema: faqSchema,
        onSubmit: values => {
            // No FAQ mutation may land while an order save is in flight
            if (savingOrderRef.current) {
                return;
            }

            if (selectedFAQ) {
                handleUpdate(values);
            } else {
                handleCreate(values);
            }
        },
        validateOnBlur: true,
        validateOnMount: true,
        enableReinitialize: true,
    });

    return (
        <Row className="justify-content-center py-md-5">
            <Col md={4}>
                <h1 className="mb-2">שאלות תשובות</h1>
            </Col>
            <Col md={8} className="d-flex justify-content-end align-items-center">
                <Button disabled={savingOrder} onClick={() => openModal(null)}>הוסף שאלה-תשובה</Button>
            </Col>
            <Col md={12}>
                <Nav variant="tabs" className="mt-3 mb-3" activeKey={activeSection}>
                    {FAQ_SECTIONS.map(section => (
                        <Nav.Item key={section}>
                            <Nav.Link
                                eventKey={section}
                                disabled={savingOrder}
                                onClick={() => handleSectionChange(section)}
                            >
                                {FAQ_SECTION_LABELS[section]}
                            </Nav.Link>
                        </Nav.Item>
                    ))}
                </Nav>
            </Col>
            <Col md={12}>
                <Table striped bordered hover>
                    <thead>
                    <tr>
                        <th>#</th>
                        <th>סדר</th>
                        <th>שאלה</th>
                        {/*<th>תיאור</th>*/}
                        <th>נוצר בתאריך</th>
                        <th>עודכן בתאריך</th>
                        <th>פעולות</th>
                    </tr>
                    </thead>
                    <tbody>
                    {faqs.map((faq, index) => (
                        <tr key={faq.id}>
                            <td>{index + 1}</td>
                            <td className="text-nowrap">
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    className="px-2 py-0 me-1"
                                    title="העבר לראש הרשימה"
                                    disabled={savingOrder || index === 0}
                                    onClick={() => handleReorder(index, 0)}
                                >
                                    ⤒
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    className="px-2 py-0 me-1"
                                    title="העבר למעלה"
                                    disabled={savingOrder || index === 0}
                                    onClick={() => handleReorder(index, index - 1)}
                                >
                                    ↑
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    className="px-2 py-0 me-1"
                                    title="העבר למטה"
                                    disabled={savingOrder || index === faqs.length - 1}
                                    onClick={() => handleReorder(index, index + 1)}
                                >
                                    ↓
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    className="px-2 py-0"
                                    title="העבר לתחתית הרשימה"
                                    disabled={savingOrder || index === faqs.length - 1}
                                    onClick={() => handleReorder(index, faqs.length - 1)}
                                >
                                    ⤓
                                </Button>
                            </td>
                            <td>{faq.title}</td>
                            {/*<td>{faq.description}</td>*/}
                            <td>{new Date(faq.createdAt).toLocaleDateString()}</td>
                            <td>
                                {faq.updatedAt &&
                                    new Date(faq.updatedAt).toLocaleDateString()}
                            </td>
                            <td>
                                <Button className="me-2" disabled={savingOrder} onClick={() => openModal(faq)}>
                                    ערוך
                                </Button>
                                <Button variant="danger" disabled={savingOrder}
                                        onClick={() => handleDelete(faq.id)}>
                                    מחק
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </Col>

            <Modal show={showModal} onHide={() => {
                setShowModal(false);
                formik.resetForm();
            }}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedFAQ ? 'עדכון' : 'הוספת'} המלצה</Modal.Title>
                </Modal.Header>
                <Form noValidate onSubmit={formik.handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-2">
                            <Form.Label>עמוד</Form.Label>
                            <Form.Select
                                name="section"
                                value={formik.values.section}
                                onChange={formik.handleChange}
                                isInvalid={formik.touched.section && !!formik.errors.section}
                            >
                                {FAQ_SECTIONS.map(section => (
                                    <option key={section} value={section}>{FAQ_SECTION_LABELS[section]}</option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.section}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>שאלה</Form.Label>
                            <Form.Control
                                type="text"
                                name="title"
                                placeholder="כותרת ההמלצה"
                                value={formik.values.title}
                                onChange={formik.handleChange}
                                isInvalid={formik.touched.title && !!formik.errors.title}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.title}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mt-2">
                            <Form.Label>תשובה</Form.Label>
                            <ReactQuill
                                className={classes.quillRtl}
                                placeholder="הכנס תשובה"
                                value={formik.values.description}
                                onChange={value => formik.setFieldValue('description', value)}
                                modules={modules}
                                formats={formats}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.description}
                            </Form.Control.Feedback>
                        </Form.Group>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            בטל
                        </Button>
                        <Button variant="primary" type="submit" disabled={savingOrder}>
                            שמור
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Row>
    );
};

export default AdminFAQ;
